/**
 * Types for the three configuration-governance backends that shipped together:
 * grading strategies (CFG-13), academic policies (CFG-12) and form schemas
 * (CFG-11) on campusone-core-service.
 *
 * Every union below is transcribed from the entities and enums in that repo —
 * src/grading-strategies/domain/grading-strategy.entity.ts,
 * src/academic-policies/domain/academic-policy.entity.ts and
 * src/form-schemas/domain/form-schema.entity.ts — not invented here. A value
 * the server adds after this build shipped still crosses the boundary (the
 * normalisers keep unknown strings), so the unions are typing aids, not gates.
 */

// ── Shared vocabulary ──────────────────────────────────────────────────────

/** The nine-value cross-service institution taxonomy (institution-type.enum.ts). */
export const INSTITUTION_TYPES = [
  'PRIMARY_SCHOOL',
  'SECONDARY_SCHOOL',
  'COMBINED_SCHOOL',
  'UNIVERSITY',
  'HIGHER_EDUCATION_INSTITUTION',
  'TRAINING_INSTITUTION',
  'VOCATIONAL_INSTITUTION',
  'SCHOOL_GROUP',
  'EDUCATION_AUTHORITY',
] as const;
export type InstitutionType = (typeof INSTITUTION_TYPES)[number];

/**
 * The five-state lifecycle policies and form schemas share. Grading strategies
 * use only three of these (no review step), but the union is one because the
 * console renders all three vocabularies with the same badge.
 */
export const LIFECYCLE_STATUSES = [
  'DRAFT',
  'READY_FOR_REVIEW',
  'APPROVED',
  'ACTIVE',
  'SUPERSEDED',
] as const;
export type LifecycleStatus = (typeof LIFECYCLE_STATUSES)[number];

// ── Grading strategies ─────────────────────────────────────────────────────

export const GRADING_MODELS = [
  'SEQUENCE_TERM_ANNUAL',
  'TERM_ANNUAL',
  'SEMESTER_AGGREGATE',
  'GPA_CGPA',
  'CONTINUOUS_ASSESSMENT',
  'COMPETENCY_VERDICT',
  'NARRATIVE_ONLY',
  'NOT_APPLICABLE',
] as const;
export type GradingModel = (typeof GRADING_MODELS)[number];

export const GRADING_SCALES = [
  'PERCENTAGE',
  'TWENTY_POINT_SCALE',
  'LETTER_GRADE',
  'GPA',
  'CGPA',
  'COMPETENT_NOT_YET_COMPETENT',
  'NARRATIVE',
  'PASS_FAIL',
  'RUBRIC',
] as const;
export type GradingScale = (typeof GRADING_SCALES)[number];

export const ROUNDING_MODES = ['NONE', 'HALF_UP', 'HALF_DOWN', 'TRUNCATE'] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

export const MISSING_RESULT_RULES = ['EXCLUDE', 'ZERO', 'BLOCK'] as const;
export type MissingResultRule = (typeof MISSING_RESULT_RULES)[number];

export type GradingStrategyStatus = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED';

export interface GradeBand {
  code: string;
  label: string;
  minInclusive: number;
  maxInclusive: number;
  gradePoints: number | null;
  description: string | null;
}

export interface HonoursRule {
  classification: string;
  minAverage: number;
}

export interface GradingStrategy {
  id: string;
  code: string;
  name: string;
  description: string | null;
  gradingModel: GradingModel;
  gradingScale: GradingScale;
  minScore: number;
  maxScore: number;
  passThreshold: number;
  bands: GradeBand[];
  rounding: RoundingMode;
  decimalPlaces: number;
  honours: HonoursRule[] | null;
  missingResultRule: MissingResultRule;
  reassessmentRule: Record<string, unknown> | null;
  applicableInstitutionTypes: string[];
  status: GradingStrategyStatus;
  version: number;
  /** null = platform catalogue entry; a UUID = one institution's own strategy. */
  institutionId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

/** POST/PATCH body — the DTO's fields, never the entity's server-owned ones. */
export interface GradingStrategyDraftRequest {
  code?: string;
  name: string;
  description?: string;
  gradingModel: GradingModel;
  gradingScale: GradingScale;
  minScore: number;
  maxScore: number;
  passThreshold: number;
  bands: {
    code: string;
    label: string;
    minInclusive: number;
    maxInclusive: number;
    gradePoints?: number | null;
    description?: string | null;
  }[];
  rounding?: RoundingMode;
  decimalPlaces?: number;
  honours?: HonoursRule[];
  missingResultRule?: MissingResultRule;
  reassessmentRule?: Record<string, unknown>;
  applicableInstitutionTypes?: string[];
  institutionId?: string;
}

/** POST :id/validate — every problem at once, as data. */
export interface ValidationReport {
  valid: boolean;
  errors: string[];
}

// ── Academic policies ──────────────────────────────────────────────────────

export const POLICY_DOMAINS = [
  'ATTENDANCE',
  'ADMISSION',
  'ENROLMENT',
  'ADD_DROP',
  'ASSESSMENT',
  'MARK_CORRECTION',
  'RESULT_PUBLICATION',
  'PROGRESSION',
  'REPETITION',
  'PROBATION',
  'RESIT',
  'GRADUATION',
  'CERTIFICATION',
  'ACADEMIC_APPEAL',
] as const;
export type PolicyDomain = (typeof POLICY_DOMAINS)[number];

export interface AcademicPolicy {
  id: string;
  policyDomain: PolicyDomain;
  title: string;
  description: string | null;
  version: number;
  status: LifecycleStatus;
  /** YYYY-MM-DD — the first day this version governs, once ACTIVE. */
  effectiveFrom: string;
  applicableInstitutionTypes: string[];
  institutionId: string | null;
  rules: Record<string, unknown>;
  exceptions: Record<string, unknown>[] | null;
  approvedBy: string | null;
  approvedAt: string | null;
  changeSummary: string | null;
  lastTransitionReason: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface AcademicPolicyDraftRequest {
  policyDomain?: PolicyDomain;
  title: string;
  description?: string;
  effectiveFrom: string;
  applicableInstitutionTypes?: string[];
  institutionId?: string;
  rules: Record<string, unknown>;
  exceptions?: Record<string, unknown>[];
  changeSummary?: string;
}

/** One append-only history row — GET :id/history, never rewritten. */
export interface ChangeEvent {
  id: string;
  /** null for the creation event: a draft comes from nowhere. */
  fromStatus: LifecycleStatus | null;
  toStatus: LifecycleStatus;
  actor: string;
  reason: string | null;
  at: string;
}

// ── Form schemas ───────────────────────────────────────────────────────────

export const FORM_DOMAINS = [
  'ADMISSION_APPLICATION',
  'STUDENT_REGISTRATION',
  'GUARDIAN_FORM',
  'STAFF_PROFILE',
  'TRANSPORT_REQUEST',
  'HOSTEL_APPLICATION',
  'PLACEMENT_APPLICATION',
  'CERTIFICATE_REQUEST',
] as const;
export type FormDomain = (typeof FORM_DOMAINS)[number];

export const FORM_FIELD_TYPES = [
  'TEXT',
  'TEXT_AREA',
  'NUMBER',
  'DATE',
  'SELECT',
  'MULTI_SELECT',
  'CHECKBOX',
  'EMAIL',
  'PHONE',
  'FILE_UPLOAD',
  'SECTION_REFERENCE',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FIELD_SENSITIVITIES = [
  'PUBLIC',
  'INTERNAL',
  'CONFIDENTIAL',
  'HEALTH',
  'SAFEGUARDING',
] as const;
export type FieldSensitivity = (typeof FIELD_SENSITIVITIES)[number];

/** Show the section/field only when an EARLIER field holds this value. */
export interface ConditionalRule {
  fieldCode: string;
  equals: string | number | boolean;
}

export interface FieldValidationRule {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  /** Must compile as a JavaScript RegExp; the validator checks. */
  pattern?: string;
  /** Required (non-empty) for SELECT and MULTI_SELECT fields. */
  allowedValues?: string[];
}

export interface DocumentSpec {
  acceptedTypes: string[];
  maxSizeMb: number;
}

export interface FormSection {
  code: string;
  title: string;
  description?: string;
  ordinal: number;
  conditionalOn?: ConditionalRule;
}

export interface FormField {
  code: string;
  sectionCode: string;
  label: string;
  fieldType: FormFieldType;
  ordinal: number;
  required: boolean;
  /** Platform floor. Institutions may not remove or weaken protected fields. */
  protected: boolean;
  sensitivity: FieldSensitivity;
  conditionalOn?: ConditionalRule;
  validation?: FieldValidationRule;
  documentSpec?: DocumentSpec;
  roleVisibility?: string[];
  defaultValue?: string | number | boolean | null;
  helpText?: string;
}

export interface FormSchema {
  id: string;
  formDomain: FormDomain;
  code: string;
  title: string;
  description: string | null;
  version: number;
  status: LifecycleStatus;
  effectiveFrom: string;
  applicableInstitutionTypes: string[];
  institutionId: string | null;
  /** The platform template whose protected fields are this schema's floor. */
  baseSchemaId: string | null;
  sections: FormSection[];
  fields: FormField[];
  approvedBy: string | null;
  approvedAt: string | null;
  changeSummary: string | null;
  lastTransitionReason: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface FormSchemaDraftRequest {
  formDomain?: FormDomain;
  code?: string;
  title: string;
  description?: string;
  effectiveFrom: string;
  applicableInstitutionTypes?: string[];
  institutionId?: string;
  baseSchemaId?: string;
  sections: FormSection[];
  fields: FormField[];
  changeSummary?: string;
}
